import { Button } from "@/components/ui/button";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { cn } from "@/lib/utils";
import { ArrowRight, X } from "lucide-react";
import * as React from "react";

const DISMISS_KEY = "shippeek-trial-banner-dismissed";

type UsageEntry = { used: number; limit: number | "unlimited" };

function usagePercent(entry: UsageEntry): number {
  if (entry.limit === "unlimited") return 0;
  if (entry.limit === 0) return 100;
  return Math.min(100, Math.round((entry.used / entry.limit) * 100));
}

function usageColor(pct: number): string {
  if (pct > 95) return "text-red-600 dark:text-red-400";
  if (pct >= 80) return "text-yellow-600 dark:text-yellow-400";
  return "text-emerald-600 dark:text-emerald-400";
}

function bgColor(maxPct: number): string {
  if (maxPct > 95) return "bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-800";
  if (maxPct >= 80) return "bg-yellow-50 border-yellow-200 dark:bg-yellow-950/40 dark:border-yellow-800";
  return "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800";
}

function daysRemaining(trialEndsAt: string | null): number | null {
  if (!trialEndsAt) return null;
  const diff = new Date(trialEndsAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function UsageStat({ label, entry }: { label: string; entry: UsageEntry }) {
  const pct = usagePercent(entry);
  return (
    <span className={cn("font-medium tabular-nums", usageColor(pct))}>
      {entry.used}/{entry.limit === "unlimited" ? "\u221e" : entry.limit} {label}
    </span>
  );
}

export function TrialBanner() {
  const { data, error } = useSubscription();
  const [dismissed, setDismissed] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(DISMISS_KEY) === "true";
  });

  const handleDismiss = React.useCallback(() => {
    setDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, "true");
  }, []);

  // Don't render if dismissed, errored, still loading, or non-trial user
  if (dismissed || error || !data) return null;
  if (data.subscription_tier !== "trial") return null;

  const days = daysRemaining(data.trial_ends_at);
  const quotePct = usagePercent(data.usage.quotes);
  const shipmentPct = usagePercent(data.usage.shipments);
  const maxPct = Math.max(quotePct, shipmentPct);
  const urgent = maxPct > 90;

  return (
    <div
      role="status"
      aria-label="Trial status"
      className={cn(
        "sticky top-0 z-50 flex items-center gap-3 border-b px-4 py-2 text-sm",
        bgColor(maxPct),
        urgent && "animate-pulse",
      )}
    >
      {/* Trial days */}
      <span className="shrink-0 font-semibold">
        Trial{days !== null ? `: ${days} day${days !== 1 ? "s" : ""} left` : ""}
      </span>

      <span className="hidden text-muted-foreground sm:inline" aria-hidden="true">|</span>

      {/* Usage stats - hidden on very small screens */}
      <div className="hidden items-center gap-2 sm:flex">
        <UsageStat label="quotes" entry={data.usage.quotes} />
        <span className="text-muted-foreground" aria-hidden="true">&middot;</span>
        <UsageStat label="shipments" entry={data.usage.shipments} />
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Upgrade CTA */}
      <Button
        variant="default"
        size="sm"
        className="shrink-0"
        onClick={() => {
          window.dispatchEvent(new CustomEvent("shippeek:open-upgrade-modal"));
        }}
      >
        Upgrade
        <ArrowRight className="size-3.5" />
      </Button>

      {/* Dismiss */}
      <button
        type="button"
        aria-label="Dismiss trial banner"
        className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
        onClick={handleDismiss}
      >
        <X className="size-4" />
      </button>
    </div>
  );
}
